import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
	AppBar,
	Button,
	Container,
	Toolbar,
	Typography,
	Card,
	CardHeader,
	CardContent,
	MenuItem,
	InputAdornment
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { isEqual } from 'lodash'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { reloadFacilities } from '../../../actions/facilities/facilities.actions'
import { showError } from '../../../actions/misc/misc.actions'
import StyledPreviewText from '../../../components/Common/StyledPreviewText'
import DaysOrHoursBeforeEntry from '../../../components/Common/DaysOrHoursBeforeEntry'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import {
	NON_BREAKING_SPACE,
	TRAINING,
	FEE_VALUE_UNITS,
	PERCENT,
	CENTS,
	managePercentEntry,
	formatHoursOrDaysBeforeText,
	formatNumsToDollars
} from '../../../utils'
import StyledSwitchWithLabel from '../../../components/Common/StyledSwitchWithLabel'
import StyledCurrencyTextField from '../../../components/Common/StyledCurrencyTextField'
import StyledTextField from '../../../components/Common/StyledTextField'
import StyledAlert from '../../../components/Common/StyledAlert'
import { gray } from '../../../utils/colors'

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: '1.5rem',
		paddingLeft: '1.5rem',
		paddingRight: '1.5rem',
		paddingBottom: '3.5rem'
	},
	sections: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem'
	},
	card: { maxWidth: '945px' },
	sectionContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: '1rem'
	},
	mainHeader: { fontWeight: 800, marginBottom: 16 },
	header: {
		fontSize: '1rem',
		fontWeight: '800',
		letterSpacing: 0.75,
		fontFamily: 'Nunito'
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		zIndex: 10000
	},
	appBarButtonsContainer: {
		[theme.breakpoints.up('md')]: {
			marginLeft: '15rem'
		}
	},
	gridDouble: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		'& > div': {
			flex: 1
		}
	},
	switchContainer: { display: 'flex', alignItems: 'flex-end', marginBottom: '1rem' },
	divider: {
		height: 0.5,
		backgroundColor: gray[400]
	},
	alertContainer: {
		display: 'flex',
		alignItems: 'flex-end'
	}
}))

const TrainingCancellation = () => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const { facility, loading, trainingServicesSetup, updateFacilityServicesByServiceLine } = useSelectedFacility()

	// making this an object so it will force a re-render in the DaysOrHoursBeforeEntry component when user hits Cancel and initialize is called again.
	const [initialFreePeriodCutOffValue, setInitialFreePeriodCutOffValue] = useState({ hours: 0 })
	const [initialMidPeriodCutOffValue, setInitialMidPeriodCutOffValue] = useState({ hours: 0 })

	const [updates, setUpdates] = useState({})

	const initialize = useCallback(() => {
		if (trainingServicesSetup) {
			const { free_period_end_hours, mid_period_end_hours } = trainingServicesSetup
			const freeHoursToInt = parseInt(free_period_end_hours, 10)
			const midPeriodHoursToInt = parseInt(mid_period_end_hours, 10)
			setInitialFreePeriodCutOffValue({ hours: freeHoursToInt })
			setInitialMidPeriodCutOffValue({ hours: midPeriodHoursToInt })
			setUpdates({ ...trainingServicesSetup })
		}
	}, [trainingServicesSetup, setUpdates, setInitialFreePeriodCutOffValue, setInitialMidPeriodCutOffValue])

	useEffect(() => {
		if (!facility) {
			dispatch(reloadFacilities())
		}
	}, [facility])

	useEffect(() => {
		initialize()
	}, [initialize])

	const saveSelectedFacility = useCallback(async () => {
		try {
			await updateFacilityServicesByServiceLine({
				service_line: TRAINING,
				updates,
				facility_id: facility._id
			})
		} catch (error) {
			dispatch(showError(error || 'An error occured while updating the selected Facility.'))
		}
	}, [dispatch, facility, updates, updateFacilityServicesByServiceLine])

	const clearChanges = useCallback(() => {
		initialize()
	}, [initialize])

	const midPeriodEndHoursIsGreaterThanFreePeriodEndHours = useMemo(() => {
		const { free_period_end_hours, mid_period_end_hours } = updates
		return mid_period_end_hours > free_period_end_hours
	}, [updates])

	const enableSaveCancelBar = useMemo(() => {
		let dirty = !isEqual(trainingServicesSetup, updates)
		return dirty
	}, [trainingServicesSetup, updates])

	const disableSaveButton = useMemo(() => {
		return midPeriodEndHoursIsGreaterThanFreePeriodEndHours || loading
	}, [midPeriodEndHoursIsGreaterThanFreePeriodEndHours, loading])

	const handleFieldUpdate = ({ field, targetValue, type }) => {
		const VALUE_FIELDS = ['mid_period_base_fee', 'final_period_base_fee']
		const UNIT_FIELDS = ['mid_period_base_fee_unit', 'final_period_base_fee_unit']
		setUpdates((prev) => {
			const revision = { ...prev, [field]: targetValue }
			if (VALUE_FIELDS.includes(field) && type && type === PERCENT) {
				revision[field] = managePercentEntry(targetValue)
			}
			if (UNIT_FIELDS.includes(field)) {
				const associatedValueField = field.replace('_unit', '')
				revision[associatedValueField] = null
			}
			// mid_period_end_hours and final_period_start_hours always stay in sync
			if (field === 'mid_period_end_hours') {
				revision.final_period_start_hours = targetValue
			}
			return revision
		})
	}

	const freePeriodPreviewText = useMemo(() => {
		const { free_period_end_hours } = updates
		const timeToDisplay = formatHoursOrDaysBeforeText({ hours: free_period_end_hours })
		return `By canceling ${timeToDisplay} prior to your course’s start time, you are in the Free Cancellation Period. Canceling in this period will not result in a fee.`
	}, [updates])

	const midPeriodPreviewText = useMemo(() => {
		const { free_period_end_hours, mid_period_base_fee, mid_period_base_fee_unit } = updates
		const timeToDisplay = formatHoursOrDaysBeforeText({ hours: free_period_end_hours })
		let feeText = `Canceling within ${timeToDisplay} of your course’s start time will result in a cancellation fee`
		if (mid_period_base_fee_unit === PERCENT) {
			feeText += ` equal to ${mid_period_base_fee}% of the total course cost.`
		} else {
			feeText += ` of ${formatNumsToDollars(mid_period_base_fee)}.`
		}
		return feeText
	}, [updates])

	const finalPeriodPreviewText = useMemo(() => {
		const { final_period_start_hours, final_period_base_fee, final_period_base_fee_unit } = updates
		const timeToDisplay = formatHoursOrDaysBeforeText({ hours: final_period_start_hours })
		let feeText = `Canceling within ${timeToDisplay} of your course’s start time will result in a cancellation fee`
		if (final_period_base_fee_unit === PERCENT) {
			feeText += ` equal to ${final_period_base_fee}% of the total course cost.`
		} else {
			feeText += ` of ${formatNumsToDollars(final_period_base_fee)}.`
		}
		return feeText
	}, [updates])

	return (
		<div>
			<Container className={classes.root} maxWidth="auto">
				<Typography variant="h5" className={classes.mainHeader}>
					Cancellation
				</Typography>
				<Card className={classes.card}>
					<CardHeader title="Cancellation Settings" />
					<CardContent>
						<div className={classes.switchContainer}>
							<StyledSwitchWithLabel
								label="Charge cancellation fees"
								checked={updates?.charge_cancellation_fee}
								onChecked={(e) => {
									handleFieldUpdate({
										field: 'charge_cancellation_fee',
										targetValue: e.target.checked
									})
								}}
							/>
						</div>
						<div className={classes.sections}>
							<div className={classes.sectionContentContainer}>
								<Typography className={classes.header}>Free Cancellation Period</Typography>
								<div className={classes.gridDouble}>
									<div>
										<StyledTextField
											header="Name"
											label="Name"
											variant="filled"
											value={updates?.free_period_name}
											onChange={(e) => {
												handleFieldUpdate({
													field: 'free_period_name',
													targetValue: e.target.value
												})
											}}
										/>
									</div>
									<div>{NON_BREAKING_SPACE}</div>
								</div>
								<div className={classes.gridDouble}>
									<DaysOrHoursBeforeEntry
										header="End time"
										initialHours={initialFreePeriodCutOffValue}
										onUpdated={(e) => {
											handleFieldUpdate({
												field: 'free_period_end_hours',
												targetValue: e.hours
											})
										}}
									/>
									<div>{NON_BREAKING_SPACE}</div>
								</div>
								<div className={classes.gridDouble}>
									<StyledPreviewText
										text={freePeriodPreviewText}
										header="Preview Cancellation Text"
									/>
									<div>{NON_BREAKING_SPACE}</div>
								</div>
							</div>
							{updates.charge_cancellation_fee ? (
								<>
									<div className={classes.divider} />
									<div className={classes.sectionContentContainer}>
										<Typography className={classes.header}>Cancellation Window A</Typography>
										<div className={classes.gridDouble}>
											<div>
												<StyledTextField
													header="Name"
													label="Name"
													variant="filled"
													value={updates?.mid_period_name}
													onChange={(e) => {
														handleFieldUpdate({
															field: 'mid_period_name',
															targetValue: e.target.value
														})
													}}
												/>
											</div>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
										<div className={classes.gridDouble}>
											<div>
												<StyledTextField
													label="Fee Type"
													header="Fee Type"
													variant="filled"
													select
													value={updates?.mid_period_base_fee_unit}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'mid_period_base_fee_unit',
															targetValue: e.target.value
														})
													}>
													{Object.entries(FEE_VALUE_UNITS).map(([value, key]) => {
														return (
															<MenuItem key={key} value={value}>
																{key}
															</MenuItem>
														)
													})}
												</StyledTextField>
											</div>
											<div>
												{updates?.mid_period_base_fee_unit === PERCENT ? (
													<StyledTextField
														id="mid-period-amount-percent"
														header="Fee Amount"
														variant="filled"
														label="Fee Amount"
														value={updates?.mid_period_base_fee}
														InputProps={{
															endAdornment: (
																<InputAdornment
																	className={classes.amountAdornment}
																	position="end">
																	%
																</InputAdornment>
															)
														}}
														onChange={(e) => {
															handleFieldUpdate({
																field: 'mid_period_base_fee',
																type: PERCENT,
																targetValue: e.target.value
															})
														}}
													/>
												) : (
													<StyledCurrencyTextField
														id="mid-period-amount-cents"
														header="Fee Amount"
														variant="filled"
														label="Fee Amount"
														initialValue={updates?.mid_period_base_fee}
														amountChanged={(e) => {
															handleFieldUpdate({
																field: 'mid_period_base_fee',
																type: CENTS,
																targetValue: e.inCents
															})
														}}
													/>
												)}
											</div>
										</div>
										<div className={classes.gridDouble}>
											<DaysOrHoursBeforeEntry
												header="Start time"
												viewonly
												initialHours={{ hours: updates?.free_period_end_hours }}
											/>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
										<div className={classes.gridDouble}>
											<DaysOrHoursBeforeEntry
												header="End time"
												initialHours={initialMidPeriodCutOffValue}
												onUpdated={(e) => {
													handleFieldUpdate({
														field: 'mid_period_end_hours',
														targetValue: e.hours
													})
												}}
											/>
											<div className={classes.alertContainer}>
												{midPeriodEndHoursIsGreaterThanFreePeriodEndHours ? (
													<StyledAlert
														type="warning"
														text="Cancellation Window A End Time cannot come before Free Period End Time"
													/>
												) : (
													NON_BREAKING_SPACE
												)}
											</div>
										</div>
										<div className={classes.gridDouble}>
											<StyledPreviewText
												text={midPeriodPreviewText}
												header="Preview Cancellation Text"
											/>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
									</div>
									<div className={classes.divider} />
									<div className={classes.sectionContentContainer}>
										<Typography className={classes.header}>
											Final Cancellation Period
										</Typography>
										<div className={classes.gridDouble}>
											<div>
												<StyledTextField
													header="Name"
													label="Name"
													variant="filled"
													value={updates?.final_period_name}
													onChange={(e) => {
														handleFieldUpdate({
															field: 'final_period_name',
															targetValue: e.target.value
														})
													}}
												/>
											</div>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
										<div className={classes.gridDouble}>
											<div>
												<StyledTextField
													label="Fee Type"
													header="Fee Type"
													variant="filled"
													select
													value={updates?.final_period_base_fee_unit}
													onChange={(e) =>
														handleFieldUpdate({
															field: 'final_period_base_fee_unit',
															targetValue: e.target.value
														})
													}>
													{Object.entries(FEE_VALUE_UNITS).map(([value, key]) => {
														return (
															<MenuItem key={key} value={value}>
																{key}
															</MenuItem>
														)
													})}
												</StyledTextField>
											</div>
											<div>
												{updates?.final_period_base_fee_unit === PERCENT ? (
													<StyledTextField
														id="final-period-amount-percent"
														header="Fee Amount"
														variant="filled"
														label="Fee Amount"
														value={updates?.final_period_base_fee}
														InputProps={{
															endAdornment: (
																<InputAdornment
																	className={classes.amountAdornment}
																	position="end">
																	%
																</InputAdornment>
															)
														}}
														onChange={(e) => {
															handleFieldUpdate({
																field: 'final_period_base_fee',
																type: PERCENT,
																targetValue: e.target.value
															})
														}}
													/>
												) : (
													<StyledCurrencyTextField
														id="final-period-amount-cents"
														header="Fee Amount"
														variant="filled"
														label="Fee Amount"
														initialValue={updates?.final_period_base_fee}
														amountChanged={(e) => {
															console.log('e', e)
															handleFieldUpdate({
																field: 'final_period_base_fee',
																type: CENTS,
																targetValue: e.inCents
															})
														}}
													/>
												)}
											</div>
										</div>
										<div className={classes.gridDouble}>
											<DaysOrHoursBeforeEntry
												header="Start time"
												viewonly
												initialHours={{ hours: updates?.final_period_start_hours }}
											/>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
										<div className={classes.gridDouble}>
											<StyledPreviewText
												text={finalPeriodPreviewText}
												header="Preview Cancellation Text"
											/>
											<div>{NON_BREAKING_SPACE}</div>
										</div>
									</div>
								</>
							) : null}
						</div>
					</CardContent>
				</Card>

				{enableSaveCancelBar ? (
					<AppBar position="fixed" color="primary" className={classes.appBar}>
						<Toolbar>
							<div className={classes.appBarButtonsContainer}>
								<Button
									variant="contained"
									color="secondary"
									disabled={disableSaveButton}
									style={{ marginRight: '8px' }}
									onClick={() => saveSelectedFacility()}>
									Save Changes
								</Button>
								<Button variant="outlined" color="secondary" onClick={clearChanges}>
									Cancel
								</Button>
							</div>
						</Toolbar>
					</AppBar>
				) : null}
			</Container>
			{loading ? <LoadingOverlay /> : null}
		</div>
	)
}

export default TrainingCancellation
